import React, { useState, useEffect, useMemo } from 'react'
import { PageHeader } from '../common'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { NavTabs, useErrorService, useUserService } from '@abroad/components'
import UploadQuestTicket from './UploadQuestTicket'
import UploadPolicy from './UploadPolicy'
import UploadPassport from './UploadPassport'
import UploadHeadshot from './UploadHeadshot'
import API from '../../utils/API'

const QuestTravelDocument = () => {
  const history = useHistory()
  const location = useLocation()
  const { questId } = useParams()
  const { user } = useUserService()
  const Error = useErrorService()
  const [stepConfiguration, setStepConfiguration] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Fetch step configuration to check enabled substeps
  useEffect(() => {
    const fetchStepConfiguration = async () => {
      if (!questId || !user?.id) return

      try {
        setIsLoading(true)
        const { data } = await API.quests.getTravelDocument(questId, user.id)
        if (data?.stepConfiguration?.step3) {
          setStepConfiguration(data.stepConfiguration.step3)
        }
      } catch (error) {
        Error.showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStepConfiguration()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, user?.id])

  // Map substeps to tabs
  const allTabs = useMemo(
    () => [
      {
        path: '#ticket',
        name: 'AIR TICKET',
        key: 'TICKET',
        substepKey: 'subStep1',
      },
      {
        path: '#insurance',
        name: 'INSURANCE',
        key: 'INSURANCE',
        substepKey: 'subStep2',
      },
      {
        path: '#passport',
        name: 'PASSPORT',
        key: 'PASSPORT',
        substepKey: 'subStep3',
      },
      {
        path: '#headshot',
        name: 'HEADSHOT',
        key: 'HEADSHOT',
        substepKey: 'subStep4',
      },
    ],
    [],
  )

  // Filter tabs based on enabled substeps
  const pages = useMemo(() => {
    // Wait for step configuration to load before showing tabs
    if (!stepConfiguration) return []

    return allTabs.filter((tab) => {
      const substep = stepConfiguration[tab.substepKey]
      return substep && substep.enabled === true
    })
  }, [stepConfiguration, allTabs])

  // Get the first enabled tab as default
  const defaultTab = useMemo(() => {
    if (pages.length > 0) {
      return pages[0].key
    }
    return 'TICKET'
  }, [pages])

  const [activeTab, setActiveTab] = useState(defaultTab)

  // Update active tab when defaultTab changes or handle URL hash
  useEffect(() => {
    if (isLoading || pages.length === 0) return

    // Check if there's a hash in the URL
    const hash = location?.hash?.replace('#', '').toLowerCase()
    if (hash) {
      const tab = pages.find((p) => p.path.replace('#', '') === hash)
      if (tab && tab.key) {
        setActiveTab(tab.key)
        return
      }
    }

    // Otherwise, set to default (first enabled tab)
    if (defaultTab) {
      setActiveTab(defaultTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, isLoading, pages, location?.hash])

  // Handle tab click from NavTabs component
  const handleTabClick = (tabName) => {
    // Find the tab by name and set the active tab to its key
    const tab = pages.find((p) => p.name === tabName || p.key === tabName)
    if (tab) {
      setActiveTab(tab.key)
    }
  }

  // Custom NavLink component that prevents navigation
  const CustomNavLink = ({ to, children, className, onClick, ...props }) => {
    const hash = to.replace('#', '').toLowerCase()
    const tab = pages.find((p) => p.path.replace('#', '') === hash)
    const tabKey = tab ? tab.key : hash.toUpperCase()
    const isActive = activeTab === tabKey

    return (
      <a
        href={to}
        className={`${className || ''} ${
          isActive ? 'active' : ''
        } cursor-pointer`}
        onClick={(e) => {
          e.preventDefault()
          if (onClick) onClick(e)
          if (tab) {
            handleTabClick(tab.name)
          }
        }}
        {...props}>
        {children}
      </a>
    )
  }

  // Get next enabled tab
  const getNextTab = (currentTabKey) => {
    const currentIndex = pages.findIndex((tab) => tab.key === currentTabKey)
    if (currentIndex >= 0 && currentIndex < pages.length - 1) {
      return pages[currentIndex + 1].key
    }
    return null
  }

  const renderActiveComponent = () => {
    if (isLoading) {
      return (
        <div className='text-center mt-40px'>
          <div className='spinner-border text-saffron-700' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )
    }

    switch (activeTab) {
      case 'TICKET':
      case 'AIR TICKET':
        return (
          <UploadQuestTicket
            questId={questId}
            onSaveSuccess={() => {
              const nextTab = getNextTab('TICKET')
              if (nextTab) setActiveTab(nextTab)
            }}
          />
        )
      case 'INSURANCE':
        return (
          <UploadPolicy
            questId={questId}
            onSaveSuccess={() => {
              const nextTab = getNextTab('INSURANCE')
              if (nextTab) setActiveTab(nextTab)
            }}
          />
        )
      case 'PASSPORT':
        return (
          <UploadPassport
            questId={questId}
            onSaveSuccess={() => {
              const nextTab = getNextTab('PASSPORT')
              if (nextTab) setActiveTab(nextTab)
            }}
          />
        )
      case 'HEADSHOT':
        return <UploadHeadshot questId={questId} />
      default:
        // Default to first enabled tab
        if (pages.length > 0) {
          return (
            <UploadQuestTicket
              questId={questId}
              onSaveSuccess={() => {
                const nextTab = getNextTab('TICKET')
                if (nextTab) setActiveTab(nextTab)
              }}
            />
          )
        }
        return null
    }
  }

  return (
    <div>
      <PageHeader
        title='Your Experiences'
        showBackArrow={true}
        backArrowAction={() => history.goBack()}
        version='small'
      />
      {!isLoading && pages.length > 0 && (
        <NavTabs
          pages={pages}
          pathname={
            pages.find((p) => p.key === activeTab)?.path ||
            `#${activeTab.toLowerCase()}`
          }
          hash={location?.hash}
          NavLink={CustomNavLink}
          linkClickedHandler={handleTabClick}
        />
      )}
      <div className='tab-content'>{renderActiveComponent()}</div>
    </div>
  )
}

export default QuestTravelDocument
